import { FC, useState, useEffect, ChangeEvent } from 'react';

import { toast } from 'react-toastify';

import { IAffectedWallet, useUpdateWalletStatusMutation } from '@entities/wallets';
import type { IWallet, IWalletClickhouse } from '@entities/wallets';
import { useLazyAffectedByAppliedPolicyQuery } from '@entities/wallets/api';
import { Switch } from '@mui/material';
import { FormItemWithError } from '@shared/components/Form';

import { LimitsIssueDialog } from './LimitsIssueDialog';

interface IProps {
  wallet: IWallet | IWalletClickhouse;
  refetch: () => void;
}

export const StatusToggler: FC<IProps> = ({ wallet, refetch }) => {
  const [updateWalletStatus, { error, isError, isLoading, isSuccess }] =
    useUpdateWalletStatusMutation();
  const [fetchAffectedByPolicy, { isFetching: isCheckingLimits }] =
    useLazyAffectedByAppliedPolicyQuery();

  const [localValue, setLocalValue] = useState<string | null>(wallet.status);
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const [limitsDialogOpen, setLimitsDialogOpen] = useState(false);
  const [affectedWallets, setAffectedWallets] = useState<IAffectedWallet[]>([]);

  useEffect(() => {
    if (pendingValue === null) {
      setLocalValue(wallet.status);
    }
  }, [wallet.status, pendingValue]);

  useEffect(() => {
    if (isSuccess && pendingValue !== null) {
      setLocalValue(pendingValue);
      setPendingValue(null);
      refetch();
    }
  }, [isSuccess, pendingValue, refetch]);

  useEffect(() => {
    if (isError && pendingValue !== null) {
      setPendingValue(null);
    }
  }, [isError, pendingValue]);

  const submitStatusChange = async (newValue: string) => {
    setPendingValue(newValue);
    try {
      await updateWalletStatus({ id: wallet.id, status: newValue }).unwrap();
    } catch (e) {
      setPendingValue(null);
    }
  };

  const handleChange = async (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const newValue = checked ? 'live' : 'inactive';

    if (newValue === 'inactive' && wallet.type === 'deposit') {
      try {
        const response = await fetchAffectedByPolicy({ id: wallet.id }).unwrap();
        if (response.data?.length) {
          setAffectedWallets(response.data);
          setLimitsDialogOpen(true);
          return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        // eslint-disable-next-line no-console
        console.error('Failed to check limits issue', e);
        toast.error(e.data.message);
      }
    }

    submitStatusChange(newValue);
  };

  const handleConfirmLimits = () => {
    setLimitsDialogOpen(false);
    submitStatusChange('inactive');
  };

  const handleCancelLimits = () => {
    setLimitsDialogOpen(false);
  };

  return (
    <FormItemWithError name={'status'} error={error} isError={isError}>
      <>
        <Switch
          checked={localValue === 'live'}
          onChange={handleChange}
          disabled={isLoading || pendingValue !== null || isCheckingLimits}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--accent)' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: 'var(--accent)',
            },
          }}
        />

        <LimitsIssueDialog
          open={limitsDialogOpen}
          affectedWallets={affectedWallets}
          onCancel={handleCancelLimits}
          onConfirm={handleConfirmLimits}
          confirmLoading={isLoading}
        />
      </>
    </FormItemWithError>
  );
};
